import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, ChevronLeft, ChevronRight, ArrowUpDown, RefreshCw,
  MapPin, Download, X, Globe, Building2, Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from './axiosConfig';
import { useFontSize } from './FontSizeContext';
import { useTranslation } from 'react-i18next';
import { usePermissions } from './usePermissions';
import StatusBadge from './StatusBadge';

import Swal from 'sweetalert2';

const LocationList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const isSuperAdmin = session.role?.name === 'SUPER_ADMIN';
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [showAllSites, setShowAllSites] = useState(false);
  const [orgsList, setOrgsList] = useState([]);
  const [sitesList, setSitesList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // 🎯 Ref عشان السايدبار لما يبعت الإشارة يقدر يقرا آخر داتا متحددة
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  const [filters, setFilters] = useState({
    location_id: "",
    description: "",
    type: "",
    status: "",
    site_id: ""
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'location_id', direction: 'asc' });
  const itemsPerPage = 20;
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const { canCreate, canUpdate, canDelete } = usePermissions('locations');

  const fetchLocations = useCallback(async (isAll = showAllSites) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const forceAllSites = isSuperAdmin && (selectedOrg !== "" || selectedSite !== "");
      const effectiveAllSites = isAll || forceAllSites;

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        q: searchTerm,
        all_sites: isSuperAdmin ? Boolean(effectiveAllSites) : false,
        location_id: filters.location_id,
        description: filters.description,
        type: filters.type,
        status: filters.status,
        site_id: effectiveAllSites ? (selectedSite || filters.site_id) : sessionSite,
        org_id: effectiveAllSites ? (selectedOrg || filters.org_id) : sessionOrg,
        sort_by: sortConfig.key,
        order: sortConfig.direction
      };
      Object.keys(params).forEach(key => (params[key] === "" || params[key] === undefined) && delete params[key]);
      const res = await api.get('/api/locations', { headers, params });

      setLocations(res.data.data || []);
      setTotalRecords(res.data.total || 0);
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error(t('locationList.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filters, sortConfig, t, showAllSites, sessionSite, sessionOrg, isSuperAdmin, selectedOrg, selectedSite]);

  useEffect(() => {
    const handleBulkDeleteEvent = () => {
      const ids = selectedIdsRef.current;

      // 1. تنبيه لو مش محدد حاجة
      if (ids.length === 0) {
        Swal.fire({
          icon: 'info',
          title: t('common.alert', 'Alert'),
          text: t('locationList.noSelection', 'Please select at least one location to delete.'),
          confirmButtonColor: '#005495',
          confirmButtonText: t('common.ok', 'OK')
        });
        return;
      }

      // 2. سويت الرت الخاص بالتأكيد
      Swal.fire({
        title: t('common.areYouSure', 'Are you sure?'),
        text: t('locationList.deleteConfirmText', `You are about to delete ${ids.length} locations. This action cannot be undone!`),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#005495',
        confirmButtonText: t('common.yesDelete', 'Yes, delete them!'),
        cancelButtonText: t('common.cancel', 'Cancel'),
        reverseButtons: isRTL // بيعكس أماكن الزراير في اللغات من اليمين للشمال
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            await api.delete('/api/locations/bulk', {
              headers,
              data: { ids }
            });

            // 3. رسالة النجاح
            Swal.fire({
              icon: 'success',
              title: t('common.deleted', 'Deleted!'),
              text: t('locationList.deleteSuccess', 'Locations have been deleted successfully.'),
              confirmButtonColor: '#005495',
              confirmButtonText: t('common.ok', 'OK')
            });

            setSelectedIds([]); // تفريغ التحديد
            fetchLocations();   // تحديث الجدول
          } catch (err) {
            console.error("Bulk Delete Error:", err);
            // 4. رسالة الخطأ (لو الموقع مربوط بأصل)
            Swal.fire({
              icon: 'error',
              title: t('common.error', 'Error!'),
              text: err.response?.data?.detail || t('locationList.deleteError', 'Failed to delete. Some locations are linked to assets.'),
              confirmButtonColor: '#005495',
              confirmButtonText: t('common.ok', 'OK')
            });
          }
        }
      });
    };

    window.addEventListener('GLOBAL_BULK_DELETE_HANDLER', handleBulkDeleteEvent);
    return () => window.removeEventListener('GLOBAL_BULK_DELETE_HANDLER', handleBulkDeleteEvent);
  }, [t, isRTL, fetchLocations]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const fetchInitialData = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      // تم توحيد الاستدعاء باستخدام api لتفادي مشاكل axios
      const orgsRes = await api.get('/api/organizations', { headers });
      setOrgsList(orgsRes.data);
    } catch (err) { console.error("Initial Load Error:", err); }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (selectedOrg) {
      const org = orgsList.find(o => o.org_id === selectedOrg);
      setSitesList(org ? org.sites : []);
    } else {
      setSitesList([]);
    }
    setSelectedSite("");
    setCurrentPage(1);
  }, [selectedOrg, orgsList]);

  const handleRowClick = (id) => {
    // 1. تجميع كل الفلاتر النشطة وتنقيتها من القيم الفاضية
    const activeFilters = Object.fromEntries(
      Object.entries({
        ...filters,
        searchParams: searchTerm, // 🎯 غيرنا الاسم ليطابق الباك إند
        all_sites: isSuperAdmin ? Boolean(showAllSites) : false, // تأكد من تمرير القيمة الصحيحة
        site_id: selectedSite || filters.site_id || (showAllSites ? '' : sessionSite) // تمرير السايت بدقة
      }).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );

    // 2. تحويل الأوبجيكت لـ Query String (مثال: ?status=OPERATING&searchParams=ROOM)
    const queryParams = new URLSearchParams(activeFilters).toString();

    // 3. التوجيه للشاشة مع دمج الـ Query
    navigate(`/locations/${id}${queryParams ? '?' + queryParams : ''}`);
  };

  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters({ location_id: "", description: "", type: "", status: "", site_id: "" });
    setSearchTerm("");
    setSortConfig({ key: 'location_id', direction: 'asc' });
    setCurrentPage(1);
  };

  useEffect(() => {
      const openAllSites = () => {
          if (isSuperAdmin) {
              setShowAllSites(true);
              setCurrentPage(1);
          }
      };
      const viewMySite = () => {
          setShowAllSites(false);
          setCurrentPage(1);
      };
      window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      return () => {
          window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
          window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      };
  }, [isSuperAdmin]);

  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  return (
    <div className={`flex flex-col h-full bg-surface-app text-[11px] overflow-hidden font-bold uppercase tracking-tighter shadow-inner ${isRTL ? 'font-cairo' : ''}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* 1. Header & Global Search */}
      <div className="bg-surface-card p-3 border-b shadow-sm z-10 space-y-3">
        <div className={`flex justify-between items-center`}>
          <div className={`flex items-center gap-6 ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <h2 className="text-primary font-black italic uppercase text-lg flex items-center gap-2 tracking-tighter">
              <RefreshCw size={20} className={loading ? 'animate-spin text-primary' : 'cursor-pointer text-primary'} onClick={() => fetchLocations()} />
              <MapPin size={20} className="text-primary" />
              {t('locationList.title')}
            </h2>

            <div className="relative group">
              <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-2.5 text-ink-secondary group-focus-within:text-primary transition-colors`} size={14} />
              <input
                className={`${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'} py-2 border border-line w-[400px] outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft shadow-inner rounded-sm font-black text-primary placeholder:font-normal placeholder:italic`}
                placeholder={t('locationList.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
              />
            </div>

            {(searchTerm || Object.values(filters).some(v => v !== "")) && (
              <button onClick={clearAllFilters} className="text-[10px] text-status-danger font-black uppercase hover:bg-status-danger-bg px-3 py-1.5 rounded transition-all flex items-center gap-1 border border-status-danger-bg ml-2">
                <X size={12}/> {t('locationList.btnClearAll')}
              </button>
            )}
          </div>

          <div className={`flex gap-3 ${isRTL ? 'ml-4' : 'mr-4'}`}>
            <button className="flex items-center gap-2 bg-surface-card border border-line px-6 py-2.5 rounded-sm font-black text-ink hover:bg-surface-panel uppercase tracking-tighter shadow-sm transition-all">
              <Download size={16} /> {t('locationList.btnExport')}
            </button>
            {canCreate && (
            <button
              onClick={() => navigate('/locations/new')}
              className={`bg-primary text-white px-8 py-2.5 rounded-sm font-black flex items-center gap-2 uppercase hover:bg-black transition-all shadow-lg active:scale-95`}
            >
              <Plus size={18} strokeWidth={3} /> {t('locationList.btnNewLocation')}
            </button>
            )}
          </div>
        </div>

        {/* 2. Advanced Organization/Site Filters */}
        {isSuperAdmin && (
          <div className={`flex items-center gap-4 ${isRTL ? 'mr-4' : 'ml-4'} bg-surface-panel p-2.5 rounded-sm border border-line w-fit shadow-sm`}>
            <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-l pl-6' : 'border-r pr-6'}`}>
              <Globe size={16} className="text-primary" />
              <span className="text-ink-secondary italic">Org:</span>
              <select
                className="bg-transparent outline-none font-black text-primary uppercase cursor-pointer"
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
              >
                <option value="">{t('assets.list.all_orgs', 'ALL ORGS')}</option>
                {orgsList.map(org => (<option key={org.org_id} value={org.org_id}>{org.org_id}</option>))}
              </select>
            </div>

            <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-l pl-6' : 'border-r pr-6'}`}>
              <Building2 size={16} className="text-primary" />
              <span className="text-ink-secondary italic">Site:</span>
              <select
                className="bg-transparent outline-none font-black text-primary uppercase cursor-pointer disabled:opacity-30"
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
                disabled={!selectedOrg}
              >
                <option value="">{t('assets.list.all_sites', 'ALL SITES')}</option>
                {sitesList.map(site => (<option key={site.site_id} value={site.site_id}>{site.site_id}</option>))}
              </select>
            </div>

            {(selectedOrg || selectedSite) && (
              <button
                onClick={() => {
                    setSelectedOrg(""); setSelectedSite("");
                    setCurrentPage(1);
                }}
                className="text-[10px] text-status-danger font-black uppercase hover:bg-status-danger-bg px-3 py-1 rounded transition-all flex items-center gap-1 border border-status-danger-bg"
              >
                <X size={12}/> {t('common.cancel', 'CANCEL')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 3. Main Table Content */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar bg-surface-panel">
        <div className="bg-surface-card border-2 border-line shadow-xl rounded-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-primary-dark text-white font-black uppercase sticky top-0 z-30 shadow-md">
              <tr className={isRTL ? 'flex-row-reverse' : ''}>
                {/* 🎯 مربع تحديد الكل */}
                <th className="p-3 border-r border-white/10 w-12 text-center">
                  <input
                    type="checkbox"
                    className="cursor-pointer w-4 h-4 accent-primary"
                    checked={locations.length > 0 && selectedIds.length === locations.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(locations.map(loc => loc.id));
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                  />
                </th>
                <th className="p-3 border-r border-white/10 w-12 text-center">#</th>
                {[
                  { label: t('locationList.thLocationId'), key: "location_id", width: "" },
                  { label: t('locationList.thDescription'), key: "description", width: "" },
                  { label: t('locationList.thType'), key: "type", width: "w-32" },
                  { label: t('locationList.thStatus'), key: "status", width: "w-32 text-center" },
                  { label: t('locationList.thSite'), key: "site_id", width: "w-24 text-center" }
                ].map(col => (
                  <th key={col.key} className={`p-3 border-r border-white/10 ${col.width} cursor-pointer hover:bg-primary transition-colors ${isRTL && !col.width.includes('text-center') ? 'text-right' : ''}`} onClick={() => requestSort(col.key)}>
                    <div className={`flex items-center gap-2 ${col.width.includes('text-center') ? 'justify-center' : 'justify-between'}`}>
                      {col.label}
                      <ArrowUpDown size={12} className={sortConfig.key === col.key ? "text-status-warning" : "text-white/30"} />
                    </div>
                  </th>
                ))}
              </tr>

              {/* Filters Row */}
              <tr className="bg-surface-panel shadow-inner">
                <td></td>
                <td></td>
                <td className="p-1.5 border-r border-line">
                  <input name="location_id" value={filters.location_id} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary uppercase" placeholder="..." />
                </td>
                <td className="p-1.5 border-r border-line">
                  <input name="description" value={filters.description} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary" placeholder="..." />
                </td>
                <td className="p-1.5 border-r border-line">
                  <input name="type" value={filters.type} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary uppercase" placeholder="..." />
                </td>
                <td className="p-1.5 border-r border-line">
                  <select name="status" value={filters.status} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary uppercase">
                      <option value="">{t('locationList.optAll')}</option>
                      <option value="OPERATING">{t('locationList.optOperating')}</option>
                      <option value="NOT OPERATING">NOT OPERATING</option>
                      <option value="DECOMMISSIONED">{t('locationList.optDecommissioned')}</option>
                  </select>
                </td>
                <td className="p-1.5 border-r border-line">
                  <input name="site_id" value={filters.site_id} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary uppercase text-center" placeholder="..." />
                </td>
              </tr>
            </thead>

            <tbody className="divide-y divide-line-subtle">
              {loading ? (
                <tr><td colSpan="6" className="p-20 text-center italic text-primary font-black animate-pulse text-sm uppercase tracking-widest bg-surface-panel">{t('workflow.loading', 'LOADING...')}</td></tr>
              ) : locations.length === 0 ? (
                <tr><td colSpan="6" className="p-20 text-center italic text-ink-secondary text-xs font-black uppercase bg-surface-panel border-dashed border-2 m-4">{t('dashboard.no_data', 'NO RECORDS FOUND')}</td></tr>
              ) : locations.map((loc, i) => (
                <tr
                  key={loc.id || i}
                  onClick={() => handleRowClick(loc.id)}
                  className={`hover:bg-primary-soft cursor-pointer group transition-all ${isRTL ? 'text-right' : ''}`}
                >
                  {/* 🎯 مربع تحديد الصف */}
                  <td className="p-3 border-r border-line-subtle text-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="cursor-pointer w-4 h-4 accent-primary"
                      checked={selectedIds.includes(loc.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(prev => [...prev, loc.id]);
                        } else {
                          setSelectedIds(prev => prev.filter(id => id !== loc.id));
                        }
                      }}
                    />
                  </td>
                  <td className="p-3 border-r border-line-subtle text-center text-ink-secondary font-mono">{(currentPage - 1) * itemsPerPage + i + 1}</td>
                  <td className="p-3 border-r border-line-subtle font-black text-primary uppercase group-hover:text-primary-hover transition-colors">{loc.location_id}</td>
                  <td className="p-3 border-r border-line-subtle text-ink font-bold">{loc.description}</td>
                  <td className="p-3 border-r border-line-subtle font-black text-ink-secondary italic">{loc.type}</td>
                  <td className="p-3 border-r border-line-subtle text-center">
                    <StatusBadge status={loc.status} />
                  </td>
                  <td className="p-3 border-r border-line-subtle font-black text-ink text-center uppercase">{loc.site_id || '---'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. Pagination Footer */}
      <div className={`bg-primary-dark text-white p-3 px-10 flex justify-between items-center shadow-2xl z-40 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-10 text-[10px] font-black uppercase tracking-widest ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex items-center gap-2">
            <span className="text-primary italic">{t('locationList.recordsFound', 'TOTAL RECORDS')}</span>
            <span className="bg-primary px-3 py-1 rounded-sm shadow-inner border border-white/10 text-status-warning">{totalRecords}</span>
          </div>
          <div className={`flex items-center gap-2 border-white/10 ${isRTL ? 'border-r pr-10' : 'border-l pl-10'}`}>
            <span className="text-primary italic">{t('locationList.page', 'PAGE')}</span>
            <span className="text-white">{currentPage}</span>
            <span className="text-white/30 mx-1">/</span>
            <span className="text-white">{totalPages || 1}</span>
          </div>
        </div>

        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
            className="p-2 border border-white/20 rounded-sm disabled:opacity-10 hover:bg-status-warning hover:border-status-warning transition-all shadow-md active:scale-90"
          >
            <ChevronLeft size={20} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>
          </button>
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(p => p + 1)}
            className="p-2 border border-white/20 rounded-sm disabled:opacity-10 hover:bg-status-warning hover:border-status-warning transition-all shadow-md active:scale-90"
          >
            <ChevronRight size={20} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationList;
